import React, { useMemo } from 'react';

/**
 * Componente CustomSlider - Usa input range nativo do HTML em todos os dispositivos
 * Estilizado com CSS inline para garantir visibilidade em mobile
 */
const CustomSlider = ({ 
  label, 
  value, 
  min, 
  max, 
  step, 
  onChange, 
  description,
  unit = '%',
  color = 'blue',
  formatValue = null,
  disabled = false,
  gradient = null,
  thumbColor = null
}) => {
  // Calcular valor para exibição
  const displayValue = useMemo(() => {
    if (formatValue) return formatValue(value);
    if (unit === '%') return Math.round(value * 100);
    if (typeof value === 'number') return value.toFixed(2);
    return value;
  }, [value, unit, formatValue]);

  // Mapear cores para valores hex
  const colorMap = {
    blue: '#3b82f6',
    purple: '#9333ea',
    red: '#ef4444',
    green: '#7eb715', // verde pickiwi
    emerald: '#10b981',
    orange: '#f97316',
    gray: '#6b7280'
  };

  const selectedColor = colorMap[color] || colorMap.green;
  
  // Cor da bolinha: usa thumbColor se fornecido, senão usa selectedColor
  const actualThumbColor = thumbColor || selectedColor;

  // Calcular porcentagem para o gradiente
  const percentage = useMemo(() => {
    return ((value - min) / (max - min)) * 100;
  }, [value, min, max]);

  // Gerar ID único para este slider
  const sliderId = useMemo(() => `slider-${Math.random().toString(36).substr(2, 9)}`, []);

  // Detectar se é mobile para ajustar estilos
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;

  return (
    <div className="space-y-1">
      <style>{`
        /* Webkit (Chrome, Safari, Edge, iOS) */
        #${sliderId} {
          -webkit-appearance: none;
          appearance: none;
          width: 100%;
          height: 2px;
          border-radius: 2px;
          outline: none;
        }
        
        #${sliderId}::-webkit-slider-thumb {
          -webkit-appearance: none;
          appearance: none;
          width: 18px;
          height: 18px;
          border-radius: 50%;
          background: ${actualThumbColor};
          cursor: pointer;
          border: 3px solid white;
          box-shadow: 0 2px 8px rgba(0,0,0,0.4);
          transition: all 0.15s ease;
        }
        
        #${sliderId}::-webkit-slider-thumb:active {
          transform: scale(1.2);
          box-shadow: 0 4px 12px rgba(0,0,0,0.5);
        }

        /* Firefox */
        #${sliderId}::-moz-range-thumb {
          width: 18px;
          height: 18px;
          border-radius: 50%;
          background: ${actualThumbColor};
          cursor: pointer;
          border: 3px solid white;
          box-shadow: 0 2px 8px rgba(0,0,0,0.4);
          transition: all 0.15s ease;
        }
        
        #${sliderId}::-moz-range-thumb:active {
          transform: scale(1.2);
          box-shadow: 0 4px 12px rgba(0,0,0,0.5);
        }

        #${sliderId}::-moz-range-track {
          height: 2px;
          border-radius: 2px;
        }
      `}</style>
      
      {/* Label e Valor */}
      <div className="flex items-center justify-between mb-2">
        <label className="text-sm font-normal text-white">
          {label}
        </label>
        <span className="text-xs font-normal px-3 py-1 rounded-md text-white" style={{ backgroundColor: 'transparent' }}>
          {displayValue}{unit}
        </span>
      </div>

      {/* Range Slider NATIVO com estilos inline para garantir visibilidade */}
      <input
        id={sliderId}
        type="range"
        min={min}
        max={max}
        step={step || 0.001}
        value={value}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        disabled={disabled}
        style={{
          background: gradient 
            ? (gradient.includes('linear-gradient') ? gradient : `linear-gradient(to right, ${gradient})`)
            : isMobile
              ? `linear-gradient(to right, ${selectedColor} 0%, ${selectedColor} ${percentage}%, rgba(255,255,255,0.15) ${percentage}%, rgba(255,255,255,0.15) 100%)`
              : `linear-gradient(to right, ${selectedColor} 0%, ${selectedColor} ${percentage}%, #e5e7eb ${percentage}%, #e5e7eb 100%)`,
          WebkitAppearance: 'none',
          appearance: 'none',
          width: '100%',
          height: '2px',
          borderRadius: '2px',
          outline: 'none',
          opacity: disabled ? 0.5 : 1,
        }}
      />

      {/* Descrição */}
      {description && (
        <div className="text-xs leading-relaxed mt-1 text-gray-400">
          {description}
        </div>
      )}
    </div>
  );
};

export default CustomSlider;
